"use client";
import React, { useEffect, useRef, useState } from "react";
import { FaShieldAlt, FaSpinner } from "react-icons/fa";
import { signIn, useSession } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { LegalLinks } from "@/components/LegalLinks";
import { isCapacitor } from "@/lib/mobile/platform";
import { UrlLinks } from "@/shared/config";

const OPENID_PROVIDER_ID = "securegateway";
const HAS_LOGGED_IN_KEY = "schmgt.hasLoggedIn";
// When the last automatic password re-entry started; stops a redirect loop if SecureGate keeps
// returning without a fresh password (e.g. it doesn't support prompt=login yet).
const REAUTH_ATTEMPT_KEY = "schmgt.reauthAttemptAt";
const REAUTH_RETRY_WINDOW_MS = 60_000;
// 4 h idle, or a login rejected by the auth_time check: SecureGate must ask for the password
// (prompt=login), which /auth/reauth does (docs/refer/design/sessionhandling-v1.md). The 12-hour
// end ("expired") and an ended session ("revoked") use a normal login.
const PASSWORD_REAUTH_REASONS = new Set(["idle", "reauth_password"]);
const SESSION_ENDED_REASONS = new Set(["expired", "revoked"]);
// A login refused because the account isn't set up in the app (src/lib/accountAccess.ts).
// login_error is temporary; the others are account problems a school admin must fix.
const ACCOUNT_DENIAL_REASONS = new Set(["no_org", "not_set_up", "guardian_inactive"]);

function deniedLoginMessage(
  reason: string,
  email: string | null,
  orgName: string | null
): { title: string; body: string } | null {
  const school = orgName ?? "your school";
  switch (reason) {
    case "no_org":
      return {
        title: email ? `We couldn't find a school account for ${email}` : "We couldn't find a school account for you",
        body:
          "You signed in, but this email isn't registered with any school on this app. Check that you used the email your school has on file, or contact your school office.",
      };
    case "not_set_up":
      return {
        title: `Your access to ${school} isn't set up yet`,
        body: `${email ? `${email} has` : "Your account has"} no access to this app yet. Ask your school administrator to add you. Parents: ask the school office to add this email to your child's record.`,
      };
    case "guardian_inactive":
      return {
        title: `Your parent access to ${school} isn't active`,
        body: `We found ${email ?? "your email"} on a guardian record, but it isn't active or isn't linked to a current student. Please contact the school office to update your child's record.`,
      };
    case "login_error":
      return {
        title: "We couldn't sign you in right now",
        body: "Something went wrong while checking your account. Please try again in a few minutes.",
      };
    default:
      return null;
  }
}

// Auth.js error codes sent to the login page (pages.error). Our own codes (terms_declined,
// login_incomplete) have their own messages.
function authErrorMessage(error: string | null): string | null {
  switch (error) {
    case null:
    case "terms_declined":
    case "login_incomplete":
      return null;
    case "Configuration":
      return "The sign-in service isn't responding right now. Please try again in a few minutes.";
    case "AccessDenied":
      return "Sign-in was denied. Please contact your administrator if this keeps happening.";
    default:
      return "Sign-in didn't complete. Please try again.";
  }
}

// Progressive messages shown in the button while the IDP redirect is pending.
// "warm" messages are used when the warmup ping already succeeded; "cold" when
// the IDP may still be starting up.
const WARM_MESSAGES = [
  "Connecting to authentication service...",
  "Redirecting to OpenID...",
  "Almost there...",
];
const COLD_MESSAGES = [
  "Connecting to authentication service...",
  "Waking up secure gateway...",
  "This may take a moment on first sign-in...",
  "Almost there, please wait...",
];

type LoginClientProps = {
  /** From the signed cookie set when a login was refused (src/lib/loginDenial.ts). */
  deniedEmail?: string | null;
  deniedOrgName?: string | null;
};

export default function LoginClient({ deniedEmail = null, deniedOrgName = null }: LoginClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { data: session, status } = useSession();

  const [isRedirecting, setIsRedirecting] = useState(false);
  const [autoAuthDetected, setAutoAuthDetected] = useState(false);
  const [warmupState, setWarmupState] = useState<"idle" | "warming" | "warm" | "failed">("idle");
  const [progressMessage, setProgressMessage] = useState("");
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const callbackUrl = searchParams.get("callbackUrl") || "/";
  const reauthUrl = `${UrlLinks.pages.reauth}?callbackUrl=${encodeURIComponent(callbackUrl)}`;
  const reason = searchParams.get("reason");
  const needsPassword = reason !== null && PASSWORD_REAUTH_REASONS.has(reason);
  const sessionEnded = reason !== null && SESSION_ENDED_REASONS.has(reason);
  const isSessionExpired =
    reason === "session_expired" || reason === "permission_timeout" || needsPassword || sessionEnded;
  const deniedLogin = reason ? deniedLoginMessage(reason, deniedEmail, deniedOrgName) : null;
  const accountDenied = reason !== null && ACCOUNT_DENIAL_REASONS.has(reason);
  // The user declined the terms on the gateway (see src/app/api/auth/[...nextauth]/route.ts).
  const termsDeclined = searchParams.get("error") === "terms_declined";
  // The gateway login came back without its PKCE cookie twice (src/lib/loginRetry.ts).
  const loginIncomplete = searchParams.get("error") === "login_incomplete";
  // Any other error comes from Auth.js (pages.error in src/lib/auth.ts). `Configuration` is what it
  // reports when SecureGateway can't be reached.
  const authError = authErrorMessage(searchParams.get("error"));
  const sessionExpiredMessage =
    reason === "session_expired"
      ? "Your session has expired. Please sign in again to continue."
      : reason === "permission_timeout"
        ? "Session timeout occurred while checking permissions. Please sign in again."
        : needsPassword
          ? "For your security, please enter your password again."
          : reason === "expired"
            ? "Your session has ended. Please sign in again to continue."
            : reason === "revoked"
              ? "Your session was ended. Please sign in again."
              : null;

  // Fire-and-forget warmup ping on mount to pre-warm the Cloud Run IDP.
  useEffect(() => {
    setWarmupState("warming");
    fetch("/api/auth/warmup")
      .then((r) => r.json())
      .then((d: { warmed: boolean }) => setWarmupState(d.warmed ? "warm" : "failed"))
      .catch(() => setWarmupState("failed"));
  }, []);

  // Clear schmgt.* keys after logout redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isLogoutRedirect = params.has("logout") || params.has("signout");
    if (!isLogoutRedirect) return;

    const removeSchmgtKeys = (storage: Storage) => {
      const keysToRemove: string[] = [];
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i);
        if (key?.startsWith("schmgt.")) keysToRemove.push(key);
      }
      keysToRemove.forEach((key) => storage.removeItem(key));
    };

    removeSchmgtKeys(window.localStorage);
    removeSchmgtKeys(window.sessionStorage);
  }, []);

  // Redirect authenticated users back to their destination.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isLogoutRedirect = urlParams.has("logout") || urlParams.has("signout");
    const hasNextAuthCookies =
      document.cookie.includes("next-auth.session-token") ||
      document.cookie.includes("__Secure-next-auth.session-token");

    if (session && status === "authenticated" && !isRedirecting && !isLogoutRedirect && hasNextAuthCookies) {
      // Mark that the user has successfully logged in at least once.
      localStorage.setItem(HAS_LOGGED_IN_KEY, "1");
      setIsRedirecting(true);
      setTimeout(() => {
        router.replace(callbackUrl);
      }, 200);
    }
  }, [session, status, router, callbackUrl, isRedirecting]);

  // Auto-auth (session expired auto-redirect).
  useEffect(() => {
    const autoAuth = searchParams.get("auto_auth");
    let callbackAutoAuth = false;
    const cbUrl = searchParams.get("callbackUrl");

    if (cbUrl) {
      try {
        const innerParams = new URL(cbUrl, window.location.origin).searchParams;
        callbackAutoAuth = innerParams.get("auto_auth") === "true";
      } catch {
        callbackAutoAuth = cbUrl.includes("auto_auth=true");
      }
    }

    if ((autoAuth === "true" || callbackAutoAuth) && status !== "loading" && !session) {
      setAutoAuthDetected(true);
      const timer = setTimeout(() => {
        signIn(OPENID_PROVIDER_ID, { callbackUrl });
      }, 5000);
      return () => clearTimeout(timer);
    }

    setAutoAuthDetected(false);
  }, [searchParams, session, status, callbackUrl]);

  // Start cycling through progressive messages once the user has clicked sign-in.
  const startProgressMessages = (isWarm: boolean) => {
    const messages = isWarm ? WARM_MESSAGES : COLD_MESSAGES;
    let index = 0;
    setProgressMessage(messages[0]);

    progressIntervalRef.current = setInterval(() => {
      index += 1;
      if (index < messages.length) {
        setProgressMessage(messages[index]);
      } else {
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      }
    }, 3500);
  };

  // Cleanup interval on unmount.
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);

  const handleSignIn = () => {
    setIsRedirecting(true);
    // Not set up: end the SecureGate session first, or the next login would silently reuse the
    // same Google / magic-link account and be refused again.
    if (accountDenied) {
      window.location.href = UrlLinks.api.sessionLogout;
      return;
    }
    startProgressMessages(warmupState === "warm");
    if (needsPassword && !isCapacitor()) {
      window.location.href = reauthUrl;
      return;
    }
    signIn(OPENID_PROVIDER_ID, { callbackUrl });
  };

  // After an idle end, go straight to SecureGate's password screen. The Capacitor app
  // re-authenticates through its own PKCE flow instead.
  useEffect(() => {
    if (!needsPassword || status !== "unauthenticated") return;
    if (isCapacitor()) {
      window.location.href = UrlLinks.pages.mobileEntry;
      return;
    }
    let lastAttempt = 0;
    try {
      lastAttempt = Number(sessionStorage.getItem(REAUTH_ATTEMPT_KEY) ?? 0);
    } catch {
      // storage unavailable — try once
    }
    if (Date.now() - lastAttempt < REAUTH_RETRY_WINDOW_MS) return; // just tried: leave it to the button
    try {
      sessionStorage.setItem(REAUTH_ATTEMPT_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    setIsRedirecting(true);
    window.location.href = reauthUrl;
  }, [needsPassword, status, reauthUrl]);

  if (status === "loading") {
    return (
      <div
        className="flex min-h-screen items-center justify-center text-lg text-white"
        style={{ background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)", colorScheme: "light" }}
      >
        Loading...
      </div>
    );
  }

  // Branded full-page shown while the session is resolving after IDP callback.
  if (isRedirecting && !progressMessage) {
    return (
      <div
        className="flex min-h-screen flex-col items-center justify-center"
        style={{ background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)", colorScheme: "light" }}
      >
        <div className="bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl p-10 flex flex-col items-center gap-6 max-w-sm w-full mx-4">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
            style={{ background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)" }}
          >
            <span className="text-white text-3xl">🔐</span>
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold text-gray-900">Signing you in...</p>
            <p className="text-sm text-gray-500 mt-1">Taking you to your dashboard</p>
          </div>
          {/* Animated progress bar */}
          <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                background: "linear-gradient(90deg, #14b8a6, #0d9488)",
                animation: "progressSlide 1.5s ease-in-out infinite",
              }}
            />
          </div>
          <FaSpinner className="animate-spin text-2xl" style={{ color: "#14b8a6" }} />
        </div>
        <style jsx global>{`
          @keyframes progressSlide {
            0% { width: 0%; margin-left: 0; }
            50% { width: 60%; margin-left: 20%; }
            100% { width: 0%; margin-left: 100%; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen flex flex-col justify-center items-center"
      style={{ background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)", colorScheme: "light" }}
    >
      <div className="absolute inset-0 z-0">
        <div
          className="absolute top-1/5 left-[10%] w-[60px] h-[60px] bg-white/20 rounded-full opacity-30 animate-float"
          style={{ animationDelay: "0s" }}
        />
        <div
          className="absolute top-[75%] right-[15%] w-[80px] h-[80px] bg-white/20 rounded-[20px] opacity-30 animate-float"
          style={{ animationDelay: "2s" }}
        />
        <div
          className="absolute bottom-[30%] left-[20%] w-[40px] h-[40px] bg-white/20 rotate-45 opacity-30 animate-float"
          style={{ animationDelay: "4s" }}
        />
      </div>

      <main className="relative z-10 flex flex-col items-center w-full px-4 sm:px-0">
        <div className="login-container bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl p-6 sm:p-10 w-full max-w-sm sm:max-w-md border border-gray-200 flex flex-col items-center">
          <div className="header text-center mb-8">
            <div
              className="logo w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-lg"
              style={{ background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)" }}
            >
              <span className="logo-icon text-white text-3xl">🔐</span>
            </div>
            <h1 className="title text-2xl font-bold text-gray-900 mb-1">
              {deniedLogin ? "Can't sign you in" : isSessionExpired ? "Session Expired" : "Welcome Back"}
            </h1>
            <p className="subtitle font-medium text-base" style={{ color: "#0d9488" }}>
              {deniedLogin
                ? "Your sign-in worked, but this app couldn't open your account"
                : isSessionExpired
                  ? "Please sign in again to continue"
                  : "Sign in with your organization"}
            </p>
          </div>

          {termsDeclined && (
            <div
              role="alert"
              className="w-full mb-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm text-center"
            >
              You need to accept the terms to continue. Please contact your administrator.
            </div>
          )}

          {loginIncomplete && (
            <div
              role="alert"
              className="w-full mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm text-center"
            >
              Sign-in didn&apos;t complete. Please try again in this browser.
            </div>
          )}

          {authError && (
            <div
              role="alert"
              className="w-full mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm text-center"
            >
              {authError}
            </div>
          )}

          {deniedLogin && (
            <div
              role="alert"
              className="w-full mb-4 p-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm text-center"
            >
              <p className="font-semibold mb-1 break-words">{deniedLogin.title}</p>
              <p className="m-0">{deniedLogin.body}</p>
            </div>
          )}

          {sessionExpiredMessage && (
            <div className="w-full mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm text-center">
              <div className="flex items-center justify-center gap-2">
                <span className="text-amber-600">⚠️</span>
                {sessionExpiredMessage}
              </div>
            </div>
          )}

          <div className="login-form w-full mb-6">
            {!isRedirecting ? (
              <Button
                onClick={handleSignIn}
                className="login-btn w-full py-3 text-white text-lg font-semibold rounded-xl flex items-center justify-center gap-2 shadow-lg border-none transition-all duration-200 hover:shadow-xl"
                style={{
                  background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, #0d9488 0%, #0f766e 100%)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)";
                }}
              >
                <FaShieldAlt className="text-white text-lg flex-shrink-0" />
                {accountDenied
                  ? "Use a different account"
                  : termsDeclined || loginIncomplete || authError || reason === "login_error"
                    ? "Try again"
                    : "Continue with Secure Gateway"}
              </Button>
            ) : (
              <div className="w-full flex flex-col items-center justify-center bg-teal-50 border-2 border-teal-200 rounded-xl p-6 text-center animate-in fade-in zoom-in duration-300 shadow-sm">
                <FaSpinner className="animate-spin mb-3 justify-center text-4xl" style={{ color: "#0d9488" }} />
                <h3 className="text-lg font-bold text-teal-900 mb-2">Connecting to Secure Gateway</h3>
                <p className="text-sm font-medium text-teal-700 transition-colors duration-300">
                  {progressMessage || "Generating secure session..."}
                </p>
                <div className="w-full max-w-[200px] h-1.5 mt-4 rounded-full bg-teal-200 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      background: "linear-gradient(90deg, #14b8a6, #0d9488)",
                      animation: "progressSlide 1.5s ease-in-out infinite",
                    }}
                  />
                </div>
              </div>
            )}

            {/* Subtle warmup indicator — shown only while the background ping is in flight */}
            {warmupState === "warming" && !isRedirecting && (
              <p className="text-center text-xs mt-2" style={{ color: "#99f6e4" }}>
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle"
                  style={{ background: "#5eead4", animation: "pulse 1.5s ease-in-out infinite" }}
                />
                Preparing secure connection...
              </p>
            )}

            <div
              className="gateway-info text-center mt-6 p-4 rounded-xl border"
              style={{ backgroundColor: "#f0fdfa", borderColor: "#99f6e4" }}
            >
              <p className="text-sm font-medium m-0" style={{ color: "#0f766e" }}>
                Secure authentication powered by your organization&apos;s identity provider
              </p>
            </div>

            {autoAuthDetected && (
              <div className="mt-6 w-full flex flex-col items-center bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-700 text-center animate-fade-in">
                <FaSpinner className="animate-spin mb-2 text-xl mx-auto" style={{ color: "#14b8a6" }} />
                <div className="font-semibold">Automatic login detected</div>
                <div className="text-sm mt-1">
                  You will be logged in automatically via OpenID in 5 seconds.
                  <br />Or you can click the button above to log in now.
                </div>
              </div>
            )}
          </div>

          <div className="security-features flex items-center justify-center gap-8 mt-6 pt-6 border-t border-gray-200 w-full">
            <div className="security-item flex items-center gap-2 text-gray-500 text-sm">
              <span className="security-icon text-base" style={{ color: "#14b8a6" }}>
                🔐
              </span>
              <span>SSL Encrypted</span>
            </div>
            <div className="security-item flex items-center gap-2 text-gray-500 text-sm">
              <span className="security-icon text-base" style={{ color: "#14b8a6" }}>
                ✅
              </span>
              <span>OpenID Connect</span>
            </div>
          </div>
        </div>
      </main>

      <div className="footer absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-xs sm:text-sm z-10 px-4 flex flex-col items-center gap-1 w-full">
        <LegalLinks className="text-white/90" />
        <span className="whitespace-nowrap">© 2025 Solvent-C | All rights reserved</span>
      </div>

      <style jsx global>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(10deg); }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-fade-in { animation: fadeIn 0.5s ease-in-out; }
        @keyframes fadeIn {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
